// Πρόταση τραπεζιού ως dropdown σε όλη τη γραμμή (5/11/2026).
// Κλειστό: «✨ Πρόταση τραπεζιού» (ή η επιλεγμένη πρόταση). Ανοιχτό: προτάσεις ταξινομημένες κατά
// σκορ, με τους ΛΟΓΟΥΣ (χωρητικότητα, ιστορικό πελάτη, δημοφιλής περιοχή, VIP, ένωση τραπεζιών).
// Ίδιος scorer με το «Auto assign» της λίστας (GET /auto-assign/suggest-one).
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import config from '../../config';
import i18n from '../../i18n';
import './SuggestTableSelect.css';

const tr = i18n.t.bind(i18n);
const MAX_SCORE = 90; // 30 χωρητικότητα + 25 προσωπικό + 20 εποχικό + 15 VIP

const sameSet = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  const s = new Set(a.map(Number));
  return b.every((x) => s.has(Number(x)));
};

export const tablesLabel = (opt) => (opt?.tables || []).map((t) => t.number).join(' + ');

// factors → ανθρώπινοι λόγοι (μόνο όσοι «μετράνε»)
export const reasonsOf = (opt) => {
  const out = [];
  for (const f of opt?.factors || []) {
    const m = f.meta || {};
    if (f.key === 'capacity') {
      const spare = Number(m.capacity) - Number(m.people);
      let text = tr('{{capacity}} seats for {{people}}', { capacity: m.capacity, people: m.people });
      if (spare === 0) text += ` · ${tr('exact fit')}`;
      else if (spare > 2) text += ` · ${tr('{{n}} spare', { n: spare })}`;
      out.push({ cls: spare > 2 ? 'is-warn' : 'is-good', text });
    } else if (f.key === 'personal' && f.points > 0) {
      if (Number(m.tableVisits) > 0) out.push({ cls: 'is-good', text: tr('Guest sat here {{n}}×', { n: m.tableVisits }) });
      else if (Number(m.areaVisits) > 0) {
        out.push({ cls: 'is-good', text: tr('Guest prefers {{area}} ({{n}}/{{total}} visits)', { area: m.areaName, n: m.areaVisits, total: m.visits }) });
      }
    } else if (f.key === 'seasonal' && f.points >= 10) {
      out.push({ cls: 'is-info', text: tr('Popular area now ({{p}}%)', { p: m.popularity }) });
    } else if (f.key === 'vip' && f.points >= 6) {
      out.push({ cls: 'is-info', text: tr('VIP guest ⭐{{rate}} · sought-after table', { rate: m.rate }) });
    } else if (f.key === 'combo') {
      out.push({ cls: 'is-warn', text: tr('Joined tables ({{n}})', { n: m.tables }) });
    }
  }
  return out;
};

const Chevron = () => (
  <svg className="sts-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
);

const SuggestTableSelect = ({ arrival, people, client = null, reservation = null, features = [], selectedTableIds = [], onPick, onClear, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);      // { key, options: [...] }
  const rootRef = useRef(null);

  const valid = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(String(arrival || ''));
  const featureKey = [...(features || [])].sort().join(',');
  const key = `${arrival}|${people}|${client ?? ''}|${reservation ?? ''}|${featureKey}`;
  const options = data?.key === key ? data.options : [];
  const current = options.find((o) => sameSet(o.tableIds, selectedTableIds)) || null;

  const load = async (force = false) => {
    if (!valid || (!force && data?.key === key)) return;
    setLoading(true); setError('');
    try {
      const { data: res } = await axios.get(
        tr('{{API_BASE_URL}}/auto-assign/suggest-one', { API_BASE_URL: config.API_BASE_URL }),
        { params: { arrival, people, client: client ?? '', reservation: reservation ?? '', features: featureKey } }
      );
      const list = [res?.proposal, ...(res?.alternatives || [])].filter((o) => o?.tableIds?.length);
      setData({ key, options: list });
    } catch (e) {
      console.error('suggest-one failed', e);
      setError(tr('Could not suggest a table.'));
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    if (disabled || !valid) return;
    const next = !open;
    setOpen(next);
    if (next) load();
  };

  // κλείσιμο με κλικ έξω / Escape
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const pick = (opt) => { onPick?.(opt); setOpen(false); };
  useEffect(() => { if (open) load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [key]);

  const currentReasons = current ? reasonsOf(current).map((r) => r.text).join(' · ') : '';

  return (
    <div ref={rootRef} className={`sts${open ? ' is-open' : ''}`}>
      <button type="button" className={`sts-control${current ? ' is-chosen' : ''}`} onClick={toggle}
        disabled={disabled || !valid} aria-haspopup="listbox" aria-expanded={open}>
        <span className="sts-spark" aria-hidden="true">{current ? '✓' : '✨'}</span>
        <span className="sts-text">
          {current ? (
            <>
              <span className="sts-title">{tr('Table')} {tablesLabel(current)} · {current.score} {tr('pts')}</span>
              {currentReasons && <span className="sts-sub">{currentReasons}</span>}
            </>
          ) : (
            <>
              <span className="sts-title">{tr('Suggest table')}</span>
              <span className="sts-sub">
                {!valid ? tr('Set date, time and people first') : options.length ? tr('{{n}} proposals, ranked with reasons', { n: options.length }) : tr('Ranked proposals with reasons')}
              </span>
            </>
          )}
        </span>
        {current && onClear && (
          <span role="button" tabIndex={0} className="sts-clear" title={tr('Clear')} aria-label={tr('Clear')}
            onClick={(e) => { e.stopPropagation(); onClear(); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onClear(); } }}>✕</span>
        )}
        <Chevron />
      </button>

      {open && (
        <div className="sts-panel" role="listbox">
          {loading && <div className="sts-state"><span className="sts-spinner" /> {tr('Searching…')}</div>}
          {!loading && error && <div className="sts-state is-error">{error}</div>}
          {!loading && !error && options.length === 0 && (
            <div className="sts-state">{tr('No free table found for this time.')}</div>
          )}
          {!loading && !error && options.length > 0 && (
            <>
              <div className="sts-head">{tr('Best match first')}</div>
              {options.map((opt, i) => {
                const reasons = reasonsOf(opt);
                const cap = (opt.tables || []).reduce((s, t) => s + Number(t.capacity || 0), 0) - (opt.tables.length > 1 ? 1 : 0);
                const area = opt.tables?.[0]?.area_name || '';
                const isCurrent = current && sameSet(current.tableIds, opt.tableIds);
                return (
                  <button type="button" key={opt.tableIds.join('-')} role="option" aria-selected={!!isCurrent}
                    className={`sts-item${isCurrent ? ' is-current' : ''}`} onClick={() => pick(opt)}>
                    <span className="sts-rank" aria-hidden="true">{i === 0 ? '★' : `${i + 1}.`}</span>
                    <span className="sts-main">
                      <span className="sts-tables">
                        {(opt.tables || []).map((t, j) => (
                          <React.Fragment key={t.id}>
                            {j > 0 && <span className="sts-plus">+</span>}
                            <span className="sts-table">{t.number}</span>
                          </React.Fragment>
                        ))}
                        <span className="sts-meta">{cap} {tr('seats')}{area ? ` · ${area}` : ''}</span>
                      </span>
                      {reasons.length > 0 && (
                        <span className="sts-reasons">
                          {reasons.map((r, k) => <span key={k} className={`sts-reason ${r.cls}`}>{r.text}</span>)}
                        </span>
                      )}
                    </span>
                    <span className="sts-score">
                      <b>{opt.score}</b><i>{tr('pts')}</i>
                      <span className="sts-bar"><span style={{ width: `${Math.max(4, Math.min(100, Math.round(opt.score / MAX_SCORE * 100)))}%` }} /></span>
                    </span>
                  </button>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SuggestTableSelect;
